import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import * as React from 'react';
import styled from 'styled-components';

import AuctioneerInfo from '@/components/AuctioneerInfo/AuctioneerInfo';
import ItemCard from '@/components/ItemCard/ItemCard';
import { useAppSelector } from '@/redux/hooks';
import { getConversation } from '@/redux/modules/conversation/conversations/conversation.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryCatalogId,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';

import AuctionInfo from './AuctionInfo';
import ContextHeader from './ContextHeader';

type Props = {
    conversationId: number;
};

const Context = ({ conversationId }: Props) => {
    const { itemId } = useAppSelector((state) => getConversation(state, Number(conversationId)));
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));

    const targetRef = React.useRef(null);
    const [componentOffset, setComponentOffset] = React.useState(200);
    const [initalOffset, setInitalOffset] = React.useState(200);
    const [scrolled, setScrolled] = React.useState(false);

    useScrollPosition(
        () => {
            if (!scrolled) {
                setScrolled(true);
            }
            // @ts-ignore
            if (!componentOffset && targetRef?.current?.getBoundingClientRect()?.top) {
                // @ts-ignore
                setInitalOffset(targetRef?.current?.getBoundingClientRect()?.top || 0);
            }
            // @ts-ignore
            setComponentOffset(targetRef?.current?.getBoundingClientRect()?.top || 0);
        },
        [scrolled],
        targetRef,
        false,
        50
    );

    const contextRef = React.useRef<HTMLDivElement>(null);
    // @ts-ignore
    const height = contextRef?.current?.clientHeight || 0;
    const [windowHeight, setWindowHeight] = React.useState(1500);

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const newWindowHeight = document?.documentElement?.scrollHeight;
        if (windowHeight !== newWindowHeight) {
            setWindowHeight(newWindowHeight || 0);
        }
    }

    const stick = Boolean(componentOffset <= 0) && scrolled;
    const position = componentOffset;

    // if the element nears the top of the window, stick it
    let style: { [attribute: string]: string | number } = stick
        ? { maxWidth: 224, position: 'fixed', top: 0, width: 224 }
        : {};

    // if we get to the bottom, switch to sticking to the bottom of the container
    if (windowHeight - 1116 - initalOffset + position - height <= 0 && scrolled) {
        style = { bottom: 10, position: 'absolute' };
    }

    return (
        <StyledContext ref={targetRef}>
            <StyledSticky
                ref={contextRef}
                style={style}
            >
                {Boolean(sellerId) && (
                    <StyledContainer>
                        <ContextHeader messageId="auctioneer" />
                        <AuctioneerInfo
                            hideAskQuestionBtn
                            hideFollowBtn
                            hideFollowers
                            hideImage
                            openOnBlank
                            sellerId={sellerId}
                        />
                    </StyledContainer>
                )}
                {Boolean(catalogId) && (
                    <StyledContainer>
                        <ContextHeader messageId="auction" />
                        <AuctionInfo
                            itemId={itemId}
                            openOnBlank
                        />
                    </StyledContainer>
                )}
                {Boolean(itemId) && (
                    <ShowOnPrintContainer>
                        <ItemCard
                            allowBidding
                            hideSeller
                            itemId={itemId}
                            openOnBlank
                            showBidStatus
                            showCta={!isPassed && !isSold && bidderHasBid}
                            showDate={false}
                            showEstimate
                            showImageBorder
                            showImageWithHover
                        />
                    </ShowOnPrintContainer>
                )}
            </StyledSticky>
        </StyledContext>
    );
};

export default React.memo<Props>(Context);

const StyledContainer = styled.div`
    margin-bottom: 8px;
    padding-bottom: 8px;

    @media print {
        display: none;
    }

    a {
        text-decoration: none;
    }

    &:first-child {
        @media screen {
            border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
        }
    }
`;

const ShowOnPrintContainer = styled(StyledContainer)`
    @media print {
        display: unset;
    }
`;

const StyledSticky = styled.div`
    @media print {
        display: flex;
        flex-direction: row;
    }
`;

const StyledContext = styled.div`
    padding-left: 23px;
    min-width: 247px;
    max-width: 247px;
    position: relative;
`;
